import React, { useState, useEffect, useOptimistic } from 'react';
import { useParams } from 'react-router-dom';
import { User, Mail, Phone, Smartphone, Building2, MapPin, Globe, UserPlus, UserMinus, Clock, Calendar, Briefcase, Users, GraduationCap, Search, Home, Sparkles, Share2, ExternalLink, CalendarDays, UserCheck, Settings, MessageCircle, Crown, Camera } from 'lucide-react';
import { Tooltip } from '../ui';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { getPeopleByUuid, updatePeopleCoordinates, getProfileStats } from '../../services/apiService';
import { geocodeAddress } from '../../services/geocodingService';
import { getUserContacts, addPeopleAsContact, removeUserContact, isUserContact, getContactByPeopleId } from '../../services/userContactsService';
import { getPublicAvailability, DAYS_OF_WEEK } from '../../services/agendaService';
import { getProfilePhoto, getFileUrl } from '../../services/filesService';
import { getPeopleOccupations, getPeopleSkills, getSkillsForOccupation } from '../../services/skillsService';
import { getPlacesByPeopleId, PLACE_TYPES } from '../../services/placesService';
import { findOrCreateDirectConversation, sendMessage } from '../../services/messagesService';
import { updatePeople } from '../../services/apiService';
import { isModuleActiveForUser } from '../../context/PreferencesContext';
import { getModulesByProfileTypeAndSubscription } from '../../services/tarifsService';
import { getProfileMoments, getMomentCoverUrl } from '../../services/momentsService';
import { MomentDetailPage } from '../moments';
import { useTranslation } from '../../context/LanguageContext';

// Social share icons as SVG components
const FacebookIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const XIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

const LinkedInIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

const WhatsAppIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

const TelegramIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
);

// Profile types mapping
const PROFILE_TYPES = {
    1: { code: 'STUDENT', labelKey: 'student', icon: GraduationCap, color: 'bg-blue-500' },
    3: { code: 'INDIVIDUAL', labelKey: 'individual', icon: Home, color: 'bg-gray-500' },
    4: { code: 'ARTISAN', labelKey: 'artisan', icon: Building2, color: 'bg-amber-500' },
    5: { code: 'PROFESSIONAL', labelKey: 'professional', icon: Briefcase, color: 'bg-indigo-500' },
    6: { code: 'RETIRED', labelKey: 'retired', icon: User, color: 'bg-teal-500' }
};

// Employment status mapping
const EMPLOYMENT_STATUS = {
    'JOBSEEKER': { labelKey: 'jobseeker', icon: Search, color: 'bg-green-500' },
    'EMPLOYER': { labelKey: 'employer', icon: Briefcase, color: 'bg-orange-500' }
};

/**
 * Normalize people data to handle both lowercase and uppercase keys from ColdFusion
 */
// Convert ColdFusion bit/string/boolean to JS boolean
const toBool = (val, defaultVal = false) => {
    if (val === undefined || val === null) return defaultVal;
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return val === 1;
    if (typeof val === 'string') return val === '1' || val.toLowerCase() === 'true';
    return defaultVal;
};

const normalizePeople = (people) => ({
    id: people.id || people.ID,
    peopleUuid: people.peopleUuid || people.PEOPLEUUID || people.peopleuuid,
    firstName: people.firstName || people.FIRSTNAME || people.firstname || '',
    lastName: people.lastName || people.LASTNAME || people.lastname || '',
    email: people.email || people.EMAIL || '',
    phone: people.phone || people.PHONE || '',
    gsm: people.gsm || people.GSM || '',
    company: people.company || people.COMPANY || '',
    position: people.position || people.POSITION || '',
    // Use googleAddress as primary address field
    address: people.googleAddress || people.GOOGLEADDRESS || people.googleaddress || people.address || people.ADDRESS || '',
    postalCode: people.postalCode || people.POSTALCODE || people.postalcode || '',
    city: people.city || people.CITY || '',
    website: people.website || people.WEBSITE || '',
    latitude: people.latitude || people.LATITUDE || null,
    longitude: people.longitude || people.LONGITUDE || null,
    role: (people.role || people.ROLE || 'PRIVATE').toUpperCase(),
    profileTypeId: people.profileTypeId || people.PROFILETYPEID || people.profiletypeid || null,
    employmentStatus: (people.employmentStatus || people.EMPLOYMENTSTATUS || people.employmentstatus || '').toUpperCase() || null,
    showPhone: toBool(people.showPhone ?? people.showphone ?? people.SHOWPHONE, false),
    showGsm: toBool(people.showGsm ?? people.showgsm ?? people.SHOWGSM, false),
    showEmail: toBool(people.showEmail ?? people.showemail ?? people.SHOWEMAIL, true),
    showFacebook: toBool(people.showFacebook ?? people.showfacebook ?? people.SHOWFACEBOOK, false),
    showX: toBool(people.showX ?? people.showx ?? people.SHOWX, false),
    showLinkedIn: toBool(people.showLinkedIn ?? people.showlinkedin ?? people.SHOWLINKEDIN, false),
    showWhatsApp: toBool(people.showWhatsApp ?? people.showwhatsapp ?? people.SHOWWHATSAPP, false),
    subscriptionType: people.subscriptionType ?? people.subscriptiontype ?? people.SUBSCRIPTIONTYPE ?? null,
    subscriptionName: people.subscriptionName ?? people.subscriptionname ?? people.SUBSCRIPTIONNAME ?? null,
    subscriptionBadgeTextColor: people.subscriptionBadgeTextColor ?? people.subscriptionbadgetextcolor ?? people.SUBSCRIPTIONBADGETEXTCOLOR ?? null,
    subscriptionBadgeBgColor: people.subscriptionBadgeBgColor ?? people.subscriptionbadgebgcolor ?? people.SUBSCRIPTIONBADGEBGCOLOR ?? null,
    subscriptionBadgeBorderColor: people.subscriptionBadgeBorderColor ?? people.subscriptionbadgebordercolor ?? people.SUBSCRIPTIONBADGEBORDERCOLOR ?? null,
    createdAt: people.createdAt || people.CREATEDAT || people.createdat,
    updatedAt: people.updatedAt || people.UPDATEDAT || people.updatedat
});

/**
 * MomentsTimeline - Timeline verticale des moments groupés par mois
 */
const MomentsTimeline = ({ moments, language, onViewDetail, t }) => {
    const tm = t?.moments || {};

    // Regrouper par mois (YYYY-MM)
    const grouped = moments.reduce((acc, moment) => {
        const date = new Date(moment.createdat ?? moment.createdAt ?? moment.CREATEDAT ?? Date.now());
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!acc[key]) acc[key] = { date, moments: [] };
        acc[key].moments.push({ ...moment, _date: date });
        return acc;
    }, {});

    // Trier par mois décroissant
    const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    const MONTH_NAMES = {
        fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        nl: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
    };
    const DAY_NAMES = {
        fr: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        nl: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
    };

    const lang = language?.toLowerCase() || 'fr';
    const months = MONTH_NAMES[lang] || MONTH_NAMES.fr;
    const days = DAY_NAMES[lang] || DAY_NAMES.fr;

    const getOccupationName = (moment) => {
        const key = `occupationname${language}`;
        const keyLower = `occupationname${lang}`;
        return moment[key] || moment[keyLower] || moment.occupationnamefr || moment.occupationNameFr || '';
    };

    if (sortedKeys.length === 0) {
        return (
            <div className="text-center py-12">
                <Camera className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">{tm.empty || 'Aucun moment partagé'}</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {sortedKeys.map(key => {
                const { date, moments: monthMoments } = grouped[key];
                const monthLabel = `${months[date.getMonth()]} ${date.getFullYear()}`;
                return (
                    <div key={key}>
                        {/* Month header */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0"></div>
                            <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide">
                                {monthLabel}
                            </h3>
                            <div className="flex-1 h-px bg-amber-100"></div>
                        </div>

                        {/* Moments of this month */}
                        <div className="ml-4 space-y-3">
                            {monthMoments.map((moment) => {
                                const d = moment._date;
                                const dayLabel = `${days[d.getDay()]} ${d.getDate()}`;
                                const occupationName = getOccupationName(moment);
                                const coverUrl = getMomentCoverUrl(moment);
                                const photoCount = moment.photocount ?? moment.photoCount ?? moment.photos?.length ?? 0;

                                return (
                                    <button
                                        key={moment.id}
                                        onClick={() => onViewDetail(moment.uuid)}
                                        className="w-full text-left flex items-start gap-3 p-3 rounded-xl hover:bg-amber-50 transition-colors group border border-transparent hover:border-amber-100"
                                    >
                                        {/* Bullet + vertical line */}
                                        <div className="flex flex-col items-center shrink-0 pt-1">
                                            <div className="w-2.5 h-2.5 rounded-full bg-amber-400 group-hover:bg-amber-500 transition-colors"></div>
                                            <div className="w-px flex-1 bg-amber-100 mt-1 min-h-[20px]"></div>
                                        </div>

                                        {/* Cover thumbnail */}
                                        {coverUrl ? (
                                            <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 border border-gray-200">
                                                <img
                                                    src={coverUrl}
                                                    alt={moment.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 border border-gray-200">
                                                <Camera className="w-5 h-5 text-gray-300" />
                                            </div>
                                        )}

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate group-hover:text-amber-700 transition-colors">
                                                {moment.title}
                                            </p>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                <span className="text-xs text-gray-400">{dayLabel}</span>
                                                {occupationName && (
                                                    <>
                                                        <span className="text-xs text-gray-300">·</span>
                                                        <span className="text-xs text-amber-600 flex items-center gap-1">
                                                            <Briefcase className="w-3 h-3" />
                                                            {occupationName}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            {photoCount > 0 && (
                                                <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                                    <Camera className="w-3 h-3" />
                                                    {photoCount} photo{photoCount > 1 ? 's' : ''}
                                                </p>
                                            )}
                                        </div>

                                        {/* Arrow */}
                                        <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-amber-500 transition-colors shrink-0 mt-1" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * People Detail Content Component
 * Content-only component that shows person details (without layout)
 */
const PeopleDetailContent = () => {
    const { uuid } = useParams();
    const [people, setPeople] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [geocoding, setGeocoding] = useState(false);
    const { t, language } = useTranslation();
    const [availability, setAvailability] = useState([]);
    const [loadingAvailability, setLoadingAvailability] = useState(false);

    // New states for additional data
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
    const [occupations, setOccupations] = useState([]);
    const [occupationSkills, setOccupationSkills] = useState({});
    const [userSkillIds, setUserSkillIds] = useState([]);
    const [enterprises, setEnterprises] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [profileStats, setProfileStats] = useState(null);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [sendingAddressRequest, setSendingAddressRequest] = useState(false);
    const [addressRequestSent, setAddressRequestSent] = useState(false);
    const [viewedProfileModules, setViewedProfileModules] = useState(new Set());
    const [activeTab, setActiveTab] = useState('home');
    const [profileMoments, setProfileMoments] = useState([]);
    const [detailMomentUuid, setDetailMomentUuid] = useState(null);

    // Get current user info - userId is the numeric ID
    const userId = parseInt(localStorage.getItem('userId') || '0', 10);
    const [userContacts, setUserContacts] = useState([]);
    const [contactsLoaded, setContactsLoaded] = useState(false);

    // React 19: useOptimistic for instant UI updates
    const [optimisticPeople, updateOptimisticPeople] = useOptimistic(
        people,
        (state, newData) => state ? { ...state, ...newData } : null
    );

    // Load user contacts on mount
    useEffect(() => {
        const loadContacts = async () => {
            if (userId > 0) {
                try {
                    const contacts = await getUserContacts(userId);
                    setUserContacts(contacts);
                } catch (err) {
                    console.error('Error loading user contacts:', err);
                }
            }
            setContactsLoaded(true);
        };
        loadContacts();
    }, [userId]);

    // Load all modules active for viewed profile and store as a Set of codes
    const checkViewedProfileModules = async (normalizedPeople) => {
        try {
            const profileTypeId = normalizedPeople.profileTypeId;
            const subscriptionType = normalizedPeople.subscriptionType || 'FREE';
            if (!profileTypeId) return;
            const modules = await getModulesByProfileTypeAndSubscription(profileTypeId, subscriptionType);
            const codes = new Set(
                modules.map(m => m.CODE ?? m.code ?? m.moduleCode ?? m.modulecode ?? m.MODULECODE).filter(Boolean)
            );
            setViewedProfileModules(codes);
        } catch (err) {
            console.error('Error checking viewed profile modules:', err);
        }
    };

    // Load public moments for viewed profile
    const loadProfileMoments = async (peopleUuid) => {
        try {
            const data = await getProfileMoments(peopleUuid);
            setProfileMoments(data || []);
        } catch (err) {
            console.error('Error loading profile moments:', err);
        }
    };

    useEffect(() => {
        const loadPeople = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getPeopleByUuid(uuid, language);
                const normalizedPeople = normalizePeople(data);
                setPeople(normalizedPeople);

                // If address exists but coordinates don't, geocode the address
                if (normalizedPeople.address && (!normalizedPeople.latitude || !normalizedPeople.longitude)) {
                    geocodePeopleAddress(normalizedPeople);
                }

                // Load availability for this person
                loadAvailability(normalizedPeople.peopleUuid);

                // Load additional data
                loadProfilePhoto(normalizedPeople.peopleUuid);
                loadOccupationsAndSkills(normalizedPeople.peopleUuid);
                loadPlaces(normalizedPeople.id);
                loadProfileStats(normalizedPeople.peopleUuid);

                // Check which modules viewed profile has access to
                checkViewedProfileModules(normalizedPeople);

                // Load public moments for viewed profile
                loadProfileMoments(normalizedPeople.peopleUuid);
            } catch (err) {
                console.error('Error loading people:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadPeople();
    }, [uuid]);

    // Load profile photo
    const loadProfilePhoto = async (peopleUuid) => {
        try {
            const result = await getProfilePhoto(peopleUuid);
            const success = result.success ?? result.SUCCESS;
            const data = result.data ?? result.DATA;

            if (success && data) {
                const filePath = data.filePath ?? data.FILEPATH ?? data.filepath;
                if (filePath) {
                    setProfilePhotoUrl(getFileUrl(filePath));
                }
            }
        } catch (err) {
            console.error('Error loading profile photo:', err);
        }
    };

    // Load occupations and their skills
    const loadOccupationsAndSkills = async (peopleUuid) => {
        try {
            // Load occupations
            const occupationsData = await getPeopleOccupations(peopleUuid, language);
            setOccupations(occupationsData);

            // Load user's selected skill IDs
            const skillsData = await getPeopleSkills(peopleUuid, null, language);
            setUserSkillIds(skillsData.skillIds || []);

            // Load skills for each occupation
            const skillsMap = {};
            for (const occ of occupationsData) {
                const skills = await getSkillsForOccupation(occ.id, language);
                skillsMap[occ.id] = skills;
            }
            setOccupationSkills(skillsMap);
        } catch (err) {
            console.error('Error loading occupations/skills:', err);
        }
    };

    // Load places (enterprises and clubs)
    const loadPlaces = async (peopleId) => {
        try {
            const enterprisesData = await getPlacesByPeopleId(peopleId, PLACE_TYPES.ENTERPRISE);
            setEnterprises(enterprisesData || []);

            const clubsData = await getPlacesByPeopleId(peopleId, PLACE_TYPES.CLUB);
            setClubs(clubsData || []);
        } catch (err) {
            console.error('Error loading places:', err);
        }
    };

    // Load profile statistics
    const loadProfileStats = async (peopleUuid) => {
        try {
            const stats = await getProfileStats(peopleUuid);
            setProfileStats(stats);
        } catch (err) {
            console.error('Error loading profile stats:', err);
        }
    };

    const loadAvailability = async (peopleUuid) => {
        if (!peopleUuid) return;

        setLoadingAvailability(true);
        try {
            const data = await getPublicAvailability(peopleUuid);
            setAvailability(data || []);
        } catch (err) {
            console.error('Error loading availability:', err);
            // Don't show error to user, just no availability displayed
        } finally {
            setLoadingAvailability(false);
        }
    };

    const geocodePeopleAddress = async (peopleData) => {
        setGeocoding(true);

        // React 19: Optimistically update UI immediately
        updateOptimisticPeople({
            latitude: 'loading',
            longitude: 'loading'
        });

        try {
            console.log('Geocoding address for people:', peopleData.address);
            const coordinates = await geocodeAddress(peopleData.address);

            // Update coordinates in database
            await updatePeopleCoordinates(peopleData.peopleUuid, coordinates.latitude, coordinates.longitude);

            // Update actual state (optimistic state will automatically sync)
            setPeople(prev => ({
                ...prev,
                latitude: coordinates.latitude,
                longitude: coordinates.longitude
            }));

            console.log('Coordinates updated successfully');
        } catch (err) {
            console.error('Error geocoding address:', err);
            // useOptimistic will automatically rollback on error
        } finally {
            setGeocoding(false);
        }
    };

    // Check if this person is in user's contacts (by peopleuuid)
    const isContactAdded = (peopleUuid) => {
        if (!contactsLoaded || !userContacts || userContacts.length === 0) return false;
        // Contacts use peopleuuid (UUID string) to link to peoples table
        return userContacts.some(contact => {
            const contactPeopleUuid = contact.peopleuuid || contact.PEOPLEUUID;
            return contactPeopleUuid === peopleUuid;
        });
    };

    // Get the contact record for this person (to get contact.id for removal)
    const getContactRecord = (peopleUuid) => {
        if (!userContacts || userContacts.length === 0) return null;
        // Contacts use peopleuuid (UUID string) to link to peoples table
        return userContacts.find(contact => {
            const contactPeopleUuid = contact.peopleuuid || contact.PEOPLEUUID;
            return contactPeopleUuid === peopleUuid;
        });
    };

    const handleToggleContact = async () => {
        if (!displayPeople || userId <= 0) return;

        const existingContact = getContactRecord(displayPeople.peopleUuid);

        if (existingContact) {
            // Remove contact using the contact.id (not the people.id)
            const success = await removeUserContact(userId, existingContact.id);
            if (success) {
                // Update local state
                setUserContacts(prev => prev.filter(c => c.id !== existingContact.id));
            }
        } else {
            // Add as contact
            const newContact = await addPeopleAsContact(userId, displayPeople);
            if (newContact) {
                // Update local state
                setUserContacts(prev => [...prev, newContact]);
            }
        }
    };

    // Handle privacy toggle change
    const handlePrivacyToggle = async (field, value) => {
        if (!people) return;
        const updates = {
            showPhone: people.showPhone ? 1 : 0,
            showGsm: people.showGsm ? 1 : 0,
            showEmail: people.showEmail ? 1 : 0,
            showFacebook: people.showFacebook ? 1 : 0,
            showX: people.showX ? 1 : 0,
            showLinkedIn: people.showLinkedIn ? 1 : 0,
            showWhatsApp: people.showWhatsApp ? 1 : 0,
            [field]: value ? 1 : 0
        };
        // Update local state immediately
        setPeople(prev => ({ ...prev, [field]: value }));
        try {
            await updatePeople(people.peopleUuid, updates);
        } catch (err) {
            console.error('Error updating privacy settings:', err);
            // Revert on error
            setPeople(prev => ({ ...prev, [field]: !value }));
        }
    };

    // Handle "Demander l'adresse" button
    const handleRequestAddress = async () => {
        if (!displayPeople || userId <= 0 || sendingAddressRequest) return;
        setSendingAddressRequest(true);
        try {
            const dmResult = await findOrCreateDirectConversation(displayPeople.id);
            if (dmResult.success || dmResult.SUCCESS) {
                const conversationId = dmResult.conversationId ?? dmResult.CONVERSATIONID ?? dmResult.data?.conversationId ?? dmResult.DATA?.CONVERSATIONID;
                if (conversationId) {
                    const addressMsg = (t.peopleDetail?.addressRequestMessage || 'Hello {name}, could you please share your address with me?')
                        .replace('{name}', displayPeople.firstName);
                    await sendMessage(conversationId, addressMsg);
                    setAddressRequestSent(true);
                }
            }
        } catch (err) {
            console.error('Error requesting address:', err);
        } finally {
            setSendingAddressRequest(false);
        }
    };

    // React 19: Use optimisticPeople for display to show instant updates
    const displayPeople = optimisticPeople || people;

    // Derived helpers from viewedProfileModules Set
    const viewedProfileHasMessages = viewedProfileModules.has('MESSAGES');
    const showMomentsTab = viewedProfileModules.has('MOMENTS') && profileMoments.length > 0;

    // Privacy logic
    const isOwnProfile = displayPeople?.peopleUuid === localStorage.getItem('userUuid');
    const isPrivateAddressProfile = displayPeople && (
        [1, 3, 6].includes(Number(displayPeople.profileTypeId))
        || displayPeople.employmentStatus === 'JOBSEEKER'
    );
    const shouldHideFullAddress = isPrivateAddressProfile && !isOwnProfile;

    if (loading) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-cabaza-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 text-lg">
                    {t.common?.loading || 'Loading...'}
                </p>
            </div>
        );
    }

    if (error || !displayPeople) {
        return (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center max-w-md mx-auto">
                <p className="text-red-800">
                    {error || (t.peopleDetail?.notFound || 'Person not found')}
                </p>
            </div>
        );
    }

    const isAdded = isContactAdded(displayPeople.peopleUuid);

    return (
        <div className="bg-white">
            {/* React 19: Document Metadata */}
            {displayPeople && (
                <>
                    <title>{displayPeople.firstName} {displayPeople.lastName} - Cabaza</title>
                    <meta name="description" content={`Contact de ${displayPeople.firstName} ${displayPeople.lastName}${displayPeople.company ? ` - ${displayPeople.company}` : ''}`} />
                </>
            )}

            {/* Card */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                {/* Header - Clean and professional */}
                <div className="p-6 sm:p-8 border-b border-gray-100">
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                        {/* Profile Photo */}
                        {profilePhotoUrl ? (
                            <img
                                src={profilePhotoUrl}
                                alt={`${displayPeople.firstName} ${displayPeople.lastName}`}
                                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-gray-200 flex-shrink-0 mx-auto sm:mx-0"
                            />
                        ) : (
                            <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-100 rounded-full flex items-center justify-center border-2 border-gray-200 flex-shrink-0 mx-auto sm:mx-0">
                                <span className="text-3xl sm:text-4xl font-semibold text-gray-500">
                                    {(displayPeople.firstName?.[0] || '') + (displayPeople.lastName?.[0] || '') || '?'}
                                </span>
                            </div>
                        )}

                        <div className="flex-1 min-w-0 text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-start gap-3 mb-1">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                    {displayPeople.firstName} {displayPeople.lastName}
                                </h1>
                                {/* Add/Remove Contact Button - Next to name */}
                                {!isOwnProfile && (
                                    <Tooltip
                                        text={isAdded
                                            ? (t.peopleDetail?.removeContact || 'Remove from contacts')
                                            : (t.peopleDetail?.addContact || 'Add to contacts')}
                                        position="bottom-left"
                                    >
                                        <button
                                            onClick={handleToggleContact}
                                            className={`p-2 rounded-full transition-colors print:hidden ${
                                                isAdded
                                                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                                    : 'bg-cabaza-purple text-white hover:bg-cabaza-pink'
                                            }`}
                                        >
                                            {isAdded ? (
                                                <UserMinus className="w-5 h-5" />
                                            ) : (
                                                <UserPlus className="w-5 h-5" />
                                            )}
                                        </button>
                                    </Tooltip>
                                )}
                                {/* Settings icon - Own profile only */}
                                {isOwnProfile && (
                                    <Tooltip
                                        text={t.peopleDetail?.privacySettings || 'Privacy settings'}
                                        position="bottom-left"
                                    >
                                        <button
                                            onClick={() => setShowSettingsModal(true)}
                                            className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors print:hidden"
                                        >
                                            <Settings className="w-5 h-5" />
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                            {displayPeople.position && (
                                <p className="text-lg text-gray-600 mb-2">{displayPeople.position}</p>
                            )}
                            {/* Profile Type & Employment Status Badges */}
                            <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start mb-4">
                                {displayPeople.profileTypeId && PROFILE_TYPES[displayPeople.profileTypeId] && (
                                    <span className={`${PROFILE_TYPES[displayPeople.profileTypeId].color} text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5`}>
                                        {React.createElement(PROFILE_TYPES[displayPeople.profileTypeId].icon, { className: 'w-4 h-4' })}
                                        {t.profileTypes?.[PROFILE_TYPES[displayPeople.profileTypeId].labelKey] || PROFILE_TYPES[displayPeople.profileTypeId].labelKey}
                                    </span>
                                )}
                                {displayPeople.employmentStatus && EMPLOYMENT_STATUS[displayPeople.employmentStatus] && (
                                    <span className={`${EMPLOYMENT_STATUS[displayPeople.employmentStatus].color} text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5`}>
                                        {React.createElement(EMPLOYMENT_STATUS[displayPeople.employmentStatus].icon, { className: 'w-4 h-4' })}
                                        {t.employmentStatus?.[EMPLOYMENT_STATUS[displayPeople.employmentStatus].labelKey] || EMPLOYMENT_STATUS[displayPeople.employmentStatus].labelKey}
                                    </span>
                                )}
                                {/* Subscription Badge - always shown */}
                                <span
                                    style={{
                                        backgroundColor: displayPeople.subscriptionBadgeBgColor || '#FFFFFF',
                                        color: displayPeople.subscriptionBadgeTextColor || '#6B7280',
                                        borderColor: displayPeople.subscriptionBadgeBorderColor || '#D1D5DB',
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                    }}
                                    className="px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5"
                                >
                                    <Crown className="w-4 h-4" />
                                    {displayPeople.subscriptionName || (t.peopleDetail?.free || 'Free')}
                                </span>
                                {/* Role Badge */}
                                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5 border ${
                                    displayPeople.role === 'PUBLIC'
                                        ? 'bg-green-100 text-green-700 border-green-300'
                                        : 'bg-orange-100 text-orange-700 border-orange-300'
                                }`}>
                                    {displayPeople.role === 'PUBLIC'
                                        ? (t.peopleDetail?.public || 'Public')
                                        : (t.peopleDetail?.private || 'Private')}
                                </span>
                            </div>

                            {/* Stats Row */}
                            {profileStats && (
                                <div className="flex flex-wrap gap-4 justify-center sm:justify-start mt-3">
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <Users className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.contactCount || 0}</span>
                                        <span className="text-gray-400">{t.peopleDetail?.contacts || 'contacts'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <CalendarDays className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.appointmentCount || 0}</span>
                                        <span className="text-gray-400">{t.peopleDetail?.appointments || 'appointments'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.ownedEventCount || 0}</span>
                                        <span className="text-gray-400">{t.peopleDetail?.events || 'events'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <UserCheck className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.participatingEventCount || 0}</span>
                                        <span className="text-gray-400">{t.peopleDetail?.participations || 'participating'}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Share buttons - Hidden on print, conditional on role + toggles */}
                        {(displayPeople.role === 'PUBLIC' || isOwnProfile) &&
                         (displayPeople.showFacebook || displayPeople.showX || displayPeople.showLinkedIn || displayPeople.showWhatsApp) &&
                        (() => {
                            const shareText = [
                                `${displayPeople.firstName} ${displayPeople.lastName}`,
                                displayPeople.position,
                                displayPeople.company
                            ].filter(Boolean).join(' - ') + ' | Cabaza';
                            const shareUrl = window.location.href;
                            return (
                                <div className="flex flex-col gap-3 flex-shrink-0 print:hidden w-full sm:w-auto">
                                    <div className="flex items-center gap-2 justify-center sm:justify-start">
                                        <span className="text-xs text-gray-400 mr-1">
                                            <Share2 className="w-4 h-4 inline" />
                                        </span>
                                        {displayPeople.showFacebook && (
                                            <Tooltip text={t.peopleDetail?.shareOnFacebook || 'Share on Facebook'} position="bottom">
                                                <a
                                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 rounded-full bg-gray-100 hover:bg-blue-600 text-gray-600 hover:text-white transition-colors"
                                                >
                                                    <FacebookIcon />
                                                </a>
                                            </Tooltip>
                                        )}
                                        {displayPeople.showX && (
                                            <Tooltip text={t.peopleDetail?.shareOnX || 'Share on X'} position="bottom">
                                                <a
                                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-800 text-gray-600 hover:text-white transition-colors"
                                                >
                                                    <XIcon />
                                                </a>
                                            </Tooltip>
                                        )}
                                        {displayPeople.showLinkedIn && (
                                            <Tooltip text={t.peopleDetail?.shareOnLinkedIn || 'Share on LinkedIn'} position="bottom">
                                                <a
                                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 rounded-full bg-gray-100 hover:bg-blue-700 text-gray-600 hover:text-white transition-colors"
                                                >
                                                    <LinkedInIcon />
                                                </a>
                                            </Tooltip>
                                        )}
                                        {displayPeople.showWhatsApp && (
                                            <Tooltip text={t.peopleDetail?.shareOnWhatsApp || 'Share on WhatsApp'} position="bottom">
                                                <a
                                                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 rounded-full bg-gray-100 hover:bg-green-500 text-gray-600 hover:text-white transition-colors"
                                                >
                                                    <WhatsAppIcon />
                                                </a>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Tabs - only shown when Moments tab is available */}
                {showMomentsTab && (
                    <div className="border-b border-gray-100 px-6 sm:px-8">
                        <div className="flex gap-0">
                            <button
                                onClick={() => setActiveTab('home')}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === 'home'
                                        ? 'border-cabaza-purple text-cabaza-purple'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Home className="w-4 h-4" />
                                {t.peopleDetail?.tabHome || 'Accueil'}
                            </button>
                            <button
                                onClick={() => setActiveTab('moments')}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === 'moments'
                                        ? 'border-amber-500 text-amber-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Camera className="w-4 h-4" />
                                {t.peopleDetail?.tabMoments || 'Moments'}
                                <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                                    {profileMoments.length}
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Moments Tab Content */}
                {showMomentsTab && activeTab === 'moments' && (
                    <div className="p-6 sm:p-8">
                        <MomentsTimeline
                            moments={profileMoments}
                            language={language}
                            onViewDetail={(uuid) => setDetailMomentUuid(uuid)}
                            t={t}
                        />
                    </div>
                )}

                {/* Details - 3 columns layout with map */}
                <div className={`p-6 sm:p-8 ${showMomentsTab && activeTab === 'moments' ? 'hidden' : ''}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Column 1 - Contact Info */}
                        <div className="space-y-3">
                            {/* Company */}
                            {displayPeople.company && (
                                <div className="flex items-center gap-3">
                                    <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                                            {t.peopleDetail?.company || 'Company'}
                                        </p>
                                        <p className="text-gray-900">{displayPeople.company}</p>
                                    </div>
                                </div>
                            )}

                            {/* Email */}
                            {displayPeople.email && displayPeople.showEmail && (
                                <div className="flex items-center gap-3">
                                    <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">Email</p>
                                        <a
                                            href={`mailto:${displayPeople.email}`}
                                            className="text-gray-900 hover:text-cabaza-purple transition-colors"
                                        >
                                            {displayPeople.email}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Phone */}
                            {displayPeople.phone && displayPeople.showPhone && (
                                <div className="flex items-center gap-3">
                                    <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                                            {t.peopleDetail?.phone || 'Phone'}
                                        </p>
                                        <a
                                            href={`tel:${displayPeople.phone}`}
                                            className="text-gray-900 hover:text-cabaza-purple transition-colors"
                                        >
                                            {displayPeople.phone}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* GSM */}
                            {displayPeople.gsm && displayPeople.showGsm && (
                                <div className="flex items-center gap-3">
                                    <Smartphone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">GSM</p>
                                        <a
                                            href={`tel:${displayPeople.gsm}`}
                                            className="text-gray-900 hover:text-cabaza-purple transition-colors"
                                        >
                                            {displayPeople.gsm}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Website */}
                            {displayPeople.website && (
                                <div className="flex items-center gap-3">
                                    <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                                            {t.peopleDetail?.website || 'Website'}
                                        </p>
                                        <a
                                            href={displayPeople.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-gray-900 hover:text-cabaza-purple transition-colors break-all"
                                        >
                                            {displayPeople.website}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Column 2 - Address */}
                        <div>
                            {shouldHideFullAddress ? (
                                <>
                                    {/* Show only postal code + city for private address profiles */}
                                    {(displayPeople.postalCode || displayPeople.city) && (
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-xs text-gray-400 uppercase tracking-wide">
                                                    {t.peopleDetail?.location || 'Location'}
                                                </p>
                                                <p className="text-gray-900">
                                                    {[displayPeople.postalCode, displayPeople.city].filter(Boolean).join(' ')}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {/* Request address button - only if both users have MESSAGES module */}
                                    {isModuleActiveForUser('MESSAGES') && viewedProfileHasMessages && (
                                        !addressRequestSent ? (
                                            <button
                                                onClick={handleRequestAddress}
                                                disabled={sendingAddressRequest}
                                                className="mt-3 flex items-center gap-2 text-sm text-cabaza-purple hover:text-cabaza-pink transition-colors disabled:opacity-50"
                                            >
                                                {sendingAddressRequest ? (
                                                    <div className="w-4 h-4 border-2 border-cabaza-purple border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <MessageCircle className="w-4 h-4" />
                                                )}
                                                {t.peopleDetail?.requestAddress || 'Request address'}
                                            </button>
                                        ) : (
                                            <p className="mt-3 text-sm text-green-600 flex items-center gap-2">
                                                <MessageCircle className="w-4 h-4" />
                                                {t.peopleDetail?.requestSent || 'Request sent!'}
                                            </p>
                                        )
                                    )}
                                </>
                            ) : (
                                <>
                                    {displayPeople.address && (
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-xs text-gray-400 uppercase tracking-wide">
                                                    {t.peopleDetail?.address || 'Address'}
                                                </p>
                                                <p className="text-gray-900">{displayPeople.address}</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {/* Geocoding indicator */}
                            {geocoding && (
                                <div className="mt-3 flex items-center text-sm text-blue-600">
                                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                                    {t.peopleDetail?.geocoding || 'Geocoding...'}
                                </div>
                            )}
                        </div>

                        {/* Column 3 - Map */}
                        {optimisticPeople?.latitude && optimisticPeople?.longitude &&
                         optimisticPeople.latitude !== 'loading' && optimisticPeople.longitude !== 'loading' && (
                            <div className="print:hidden">
                                <div className="rounded-lg overflow-hidden border border-gray-200 h-32 lg:h-full lg:min-h-[140px]">
                                    <Map
                                        defaultCenter={{
                                            lat: parseFloat(optimisticPeople.latitude),
                                            lng: parseFloat(optimisticPeople.longitude)
                                        }}
                                        defaultZoom={14}
                                        mapId="cabaza-map"
                                        disableDefaultUI={true}
                                        zoomControl={false}
                                    >
                                        <AdvancedMarker
                                            position={{
                                                lat: parseFloat(optimisticPeople.latitude),
                                                lng: parseFloat(optimisticPeople.longitude)
                                            }}
                                            title={`${optimisticPeople.firstName} ${optimisticPeople.lastName}`}
                                        />
                                    </Map>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Availability Section */}
                    {loadingAvailability ? (
                        <div className="mt-6 bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                            <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-cabaza-purple border-t-transparent rounded-full animate-spin mr-3"></div>
                                <p className="text-gray-600">
                                    {t.peopleDetail?.loadingAvailability || 'Loading availability...'}
                                </p>
                            </div>
                        </div>
                    ) : availability.length > 0 ? (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-cabaza-purple" />
                                {t.peopleDetail?.availability || 'Availability'}
                            </h3>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {DAYS_OF_WEEK.map(day => {
                                        const daySlots = availability.filter(slot => slot.dayOfWeek === day.id);
                                        if (daySlots.length === 0) return null;

                                        return (
                                            <div key={day.id} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                <div className="font-medium text-cabaza-purple mb-2 flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    {day[`label_${language}`] || day.labelFr || day.label}
                                                </div>
                                                <div className="space-y-1">
                                                    {daySlots.map((slot, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="text-sm text-gray-700 bg-green-50 px-2 py-1 rounded flex items-center gap-1"
                                                        >
                                                            <Clock className="w-3 h-3 text-green-600" />
                                                            <span className="font-medium text-green-700">
                                                                {slot.startTime?.substring(0, 5)} - {slot.endTime?.substring(0, 5)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-500 mt-3 text-center">
                                    {t.peopleDetail?.availabilityDescription || 'These slots indicate the usual availability of this contact.'}
                                </p>
                            </div>
                        </div>
                    ) : null}

                    {/* Occupations and Skills Section */}
                    {occupations.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Briefcase className="w-5 h-5 text-cabaza-purple" />
                                {t.peopleDetail?.occupationsAndSkills || 'Occupations and skills *'}
                            </h3>
                            <div className="space-y-6">
                                {occupations.map(occupation => {
                                    const skills = occupationSkills[occupation.id] || [];
                                    const selectedSkills = skills.filter(s => userSkillIds.includes(s.id));
                                    const essentialSkills = selectedSkills.filter(s => s.isEssential);
                                    const optionalSkills = selectedSkills.filter(s => !s.isEssential);
                                    const occupationName = occupation[`name_${language}`] || occupation.name_fr || occupation.name_en || '';

                                    return (
                                        <div key={occupation.id} className="border-l-4 border-cabaza-purple pl-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-gray-900 text-lg">{occupationName}</h4>
                                                {occupation.isPrimary && (
                                                    <span className="text-xs text-cabaza-purple font-medium">
                                                        {t.peopleDetail?.primary || '(Primary)'}
                                                    </span>
                                                )}
                                            </div>
                                            {selectedSkills.length > 0 ? (
                                                <div className="space-y-2">
                                                    {/* Essential skills */}
                                                    {essentialSkills.length > 0 && (
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">
                                                                <Sparkles className="w-3 h-3 text-amber-500" />
                                                                {t.peopleDetail?.essentialSkills || 'Essential skills:'}
                                                            </p>
                                                            <ul className="list-disc list-inside text-gray-700 text-sm space-y-0.5 ml-2">
                                                                {essentialSkills.map(skill => {
                                                                    const skillName = skill[`name_${language}`] || skill.name_fr || skill.name_en || '';
                                                                    return <li key={skill.id}>{skillName}</li>;
                                                                })}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {/* Optional skills */}
                                                    {optionalSkills.length > 0 && (
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-600 mb-1">
                                                                {t.peopleDetail?.otherSkills || 'Other skills:'}
                                                            </p>
                                                            <ul className="list-disc list-inside text-gray-700 text-sm space-y-0.5 ml-2">
                                                                {optionalSkills.map(skill => {
                                                                    const skillName = skill[`name_${language}`] || skill.name_fr || skill.name_en || '';
                                                                    return <li key={skill.id}>{skillName}</li>;
                                                                })}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-sm text-gray-500 italic">
                                                    {t.peopleDetail?.noSkillsSelected || 'No skills selected'}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Enterprises Section */}
                    {enterprises.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-gray-500" />
                                {t.peopleDetail?.companies || 'Companies'}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {enterprises.map(enterprise => (
                                    <div key={enterprise.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        {/* Header: Name + Firm Type */}
                                        <div className="mb-2">
                                            <p className="font-semibold text-gray-900">{enterprise.placeName || enterprise.name}</p>
                                            {enterprise.firmType && (
                                                <p className="text-xs text-cabaza-purple font-medium">{enterprise.firmType}</p>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="space-y-1 text-sm text-gray-600">
                                            {/* TVA */}
                                            {enterprise.tvaNum && (
                                                <p><span className="font-medium text-gray-700">TVA:</span> {enterprise.tvaNum}</p>
                                            )}

                                            {/* Address */}
                                            {enterprise.googleAddress && (
                                                <p className="flex items-start gap-1">
                                                    <MapPin className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                                                    <span>{enterprise.googleAddress}</span>
                                                </p>
                                            )}

                                            {/* Phone */}
                                            {enterprise.phone && (
                                                <p className="flex items-center gap-1">
                                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`tel:${enterprise.phone}`} className="hover:text-cabaza-purple">
                                                        {enterprise.phone}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Email */}
                                            {enterprise.email && (
                                                <p className="flex items-center gap-1">
                                                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`mailto:${enterprise.email}`} className="text-cabaza-purple hover:underline truncate">
                                                        {enterprise.email}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Website */}
                                            {enterprise.website && (
                                                <p className="flex items-center gap-1">
                                                    <Globe className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={enterprise.website.startsWith('http') ? enterprise.website : `https://${enterprise.website}`}
                                                       target="_blank"
                                                       rel="noopener noreferrer"
                                                       className="text-cabaza-purple hover:underline truncate">
                                                        {enterprise.website.replace(/^https?:\/\//, '')}
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Clubs Section */}
                    {clubs.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Users className="w-5 h-5 text-gray-500" />
                                {t.peopleDetail?.clubsAndAssociations || 'Clubs and associations'}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {clubs.map(club => (
                                    <div key={club.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        {/* Header: Name */}
                                        <div className="mb-2">
                                            <p className="font-semibold text-gray-900">{club.placeName || club.name}</p>
                                        </div>

                                        {/* Details */}
                                        <div className="space-y-1 text-sm text-gray-600">
                                            {/* Address */}
                                            {club.googleAddress && (
                                                <p className="flex items-start gap-1">
                                                    <MapPin className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                                                    <span>{club.googleAddress}</span>
                                                </p>
                                            )}

                                            {/* Phone */}
                                            {club.phone && (
                                                <p className="flex items-center gap-1">
                                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`tel:${club.phone}`} className="hover:text-cabaza-purple">
                                                        {club.phone}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Email */}
                                            {club.email && (
                                                <p className="flex items-center gap-1">
                                                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`mailto:${club.email}`} className="text-cabaza-purple hover:underline truncate">
                                                        {club.email}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Website */}
                                            {club.website && (
                                                <p className="flex items-center gap-1">
                                                    <Globe className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={club.website.startsWith('http') ? club.website : `https://${club.website}`}
                                                       target="_blank"
                                                       rel="noopener noreferrer"
                                                       className="text-cabaza-purple hover:underline truncate">
                                                        {club.website.replace(/^https?:\/\//, '')}
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ESCO Footer Note */}
                    {occupations.length > 0 && (
                        <div className="mt-8 pt-4 border-t border-gray-100">
                            <p className="text-xs text-gray-400 text-center">
                                * {t.peopleDetail?.escoFootnote || 'Classification based on the European ESCO framework (European Skills, Competences, Qualifications and Occupations)'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Moment Detail overlay */}
            {detailMomentUuid && (
                <MomentDetailPage
                    momentUuid={detailMomentUuid}
                    onClose={() => setDetailMomentUuid(null)}
                />
            )}

            {/* Privacy Settings Modal */}
            {showSettingsModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 print:hidden" onClick={() => setShowSettingsModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Settings className="w-5 h-5 text-cabaza-purple" />
                                {t.peopleDetail?.settings || 'Settings'}
                            </h3>
                            <button
                                onClick={() => setShowSettingsModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="space-y-4">
                            {/* Show Phone toggle */}
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <Phone className="w-4 h-4 text-gray-500" />
                                    <span className="text-sm text-gray-700">
                                        {t.peopleDetail?.showPhone || 'Show my phone'}
                                    </span>
                                </div>
                                <div
                                    className={`relative w-11 h-6 rounded-full transition-colors ${displayPeople.showPhone ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                                    onClick={() => handlePrivacyToggle('showPhone', !displayPeople.showPhone)}
                                >
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${displayPeople.showPhone ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                            {/* Show GSM toggle */}
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <Smartphone className="w-4 h-4 text-gray-500" />
                                    <span className="text-sm text-gray-700">
                                        {t.peopleDetail?.showGsm || 'Show my GSM'}
                                    </span>
                                </div>
                                <div
                                    className={`relative w-11 h-6 rounded-full transition-colors ${displayPeople.showGsm ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                                    onClick={() => handlePrivacyToggle('showGsm', !displayPeople.showGsm)}
                                >
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${displayPeople.showGsm ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                            {/* Show Email toggle */}
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <Mail className="w-4 h-4 text-gray-500" />
                                    <span className="text-sm text-gray-700">
                                        {t.peopleDetail?.showEmail || 'Show my email'}
                                    </span>
                                </div>
                                <div
                                    className={`relative w-11 h-6 rounded-full transition-colors ${displayPeople.showEmail ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                                    onClick={() => handlePrivacyToggle('showEmail', !displayPeople.showEmail)}
                                >
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${displayPeople.showEmail ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                            {/* Separator */}
                            <div className="border-t border-gray-200 pt-2">
                                <p className="text-xs text-gray-400 mb-3">{t.peopleDetail?.socialSharing || 'Social sharing'}</p>
                            </div>
                            {/* Show Facebook toggle */}
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <span className="w-4 h-4 text-gray-500"><FacebookIcon /></span>
                                    <span className="text-sm text-gray-700">
                                        {t.peopleDetail?.showFacebook || 'Show Facebook'}
                                    </span>
                                </div>
                                <div
                                    className={`relative w-11 h-6 rounded-full transition-colors ${displayPeople.showFacebook ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                                    onClick={() => handlePrivacyToggle('showFacebook', !displayPeople.showFacebook)}
                                >
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${displayPeople.showFacebook ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                            {/* Show X toggle */}
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <span className="w-4 h-4 text-gray-500"><XIcon /></span>
                                    <span className="text-sm text-gray-700">
                                        {t.peopleDetail?.showX || 'Show X'}
                                    </span>
                                </div>
                                <div
                                    className={`relative w-11 h-6 rounded-full transition-colors ${displayPeople.showX ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                                    onClick={() => handlePrivacyToggle('showX', !displayPeople.showX)}
                                >
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${displayPeople.showX ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                            {/* Show LinkedIn toggle */}
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <span className="w-4 h-4 text-gray-500"><LinkedInIcon /></span>
                                    <span className="text-sm text-gray-700">
                                        {t.peopleDetail?.showLinkedIn || 'Show LinkedIn'}
                                    </span>
                                </div>
                                <div
                                    className={`relative w-11 h-6 rounded-full transition-colors ${displayPeople.showLinkedIn ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                                    onClick={() => handlePrivacyToggle('showLinkedIn', !displayPeople.showLinkedIn)}
                                >
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${displayPeople.showLinkedIn ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                            {/* Show WhatsApp toggle */}
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <span className="w-4 h-4 text-gray-500"><WhatsAppIcon /></span>
                                    <span className="text-sm text-gray-700">
                                        {t.peopleDetail?.showWhatsApp || 'Show WhatsApp'}
                                    </span>
                                </div>
                                <div
                                    className={`relative w-11 h-6 rounded-full transition-colors ${displayPeople.showWhatsApp ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                                    onClick={() => handlePrivacyToggle('showWhatsApp', !displayPeople.showWhatsApp)}
                                >
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${displayPeople.showWhatsApp ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                        </div>
                        <p className="mt-4 text-xs text-gray-400">
                            {t.peopleDetail?.settingsDescription || 'These settings control what other users can see on your profile.'}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PeopleDetailContent;
